import { useState, useEffect, useRef } from 'react'
import { Input } from './input'
import { Loader2, Search, X } from 'lucide-react'
import { cn } from '../../lib/utils'

/**
 * Reusable type-ahead search component
 * 
 * @param {Object} props
 * @param {string} props.value - Selected value ID
 * @param {Function} props.onChange - Callback when selection changes (receives full item object)
 * @param {Function} props.onSearch - Async function that takes search query and returns results array
 * @param {Function} props.renderItem - Function to render each item in dropdown (receives item)
 * @param {Function} props.getItemLabel - Function to get display label from item
 * @param {Function} props.getItemValue - Function to get value/ID from item
 * @param {string} props.valueLabel - Optional label to display for the current value
 * @param {string} props.placeholder - Input placeholder text
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {string} props.emptyMessage - Message to show when no results
 */
export default function TypeAheadSearch({
  value,
  valueLabel,
  onChange,
  onSearch,
  renderItem,
  getItemLabel,
  getItemValue,
  placeholder = 'Search...',
  disabled = false,
  emptyMessage = 'No results found'
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const lastValueRef = useRef()
  
  const wrapperRef = useRef(null)
  const inputRef = useRef(null)
  const searchTimeoutRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (value === lastValueRef.current) return

    lastValueRef.current = value

    if (!value) {
      setSelectedItem(null)
      setSearchQuery('')
      return
    }

    if (valueLabel) {
      setSearchQuery(valueLabel)
    }
  }, [value, valueLabel])

  // Debounced search — fetch default results when query is empty
  useEffect(() => {
    if (!isOpen) return

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    setLoading(true)

    // Debounce: 0ms for empty query (show defaults immediately), 300ms for typed queries
    const delay = searchQuery.trim() ? 300 : 0

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const items = await onSearch(searchQuery)
        setResults(items || [])
        setHighlightedIndex(0)
      } catch (error) {
        console.error('Search error:', error)
        setResults([])
      } finally {
        setLoading(false)
      }
    }, delay)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchQuery, isOpen, onSearch])

  // Handle selection
  const handleSelect = (item) => {
    setSelectedItem(item)
    setSearchQuery(getItemLabel(item))
    setIsOpen(false)
    onChange(item)
  }

  // Handle clear
  const handleClear = () => {
    setSelectedItem(null)
    setSearchQuery('')
    setResults([])
    onChange(null)
    inputRef.current?.focus()
  }

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex((prev) => 
          prev < results.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0))
        break
      case 'Enter':
        e.preventDefault()
        if (results[highlightedIndex]) {
          handleSelect(results[highlightedIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        break
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value)
            if (!isOpen) setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-9 pr-9"
        />
        {(searchQuery || selectedItem) && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-60 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : results.length > 0 ? (
            <div className="py-1">
              {results.map((item, index) => (
                <button
                  key={getItemValue(item)}
                  type="button"
                  onClick={() => handleSelect(item)}
                  className={cn(
                    'w-full px-3 py-2.5 sm:py-2 text-left text-sm hover:bg-muted cursor-pointer transition-colors',
                    index === highlightedIndex && 'bg-muted'
                  )}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {renderItem ? renderItem(item) : getItemLabel(item)}
                </button>
              ))}
            </div>
          ) : (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              {emptyMessage}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
